import React, { useEffect, useState } from 'react';
import { Box, Chip, Typography } from '@mui/material';
import { History } from '@mui/icons-material';
import adminService, { AdminActivityLogEntry, AdminUserRecord, City } from '../../services/adminService';
import { PageHeader, ResponsiveTable, StatCard } from '../../components/common';

const AuditLog: React.FC = () => {
  const [entries, setEntries] = useState<AdminActivityLogEntry[]>([]);
  const [admins, setAdmins] = useState<AdminUserRecord[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [log, adminList, cityList] = await Promise.all([
          adminService.getAllAdminActivityLog(),
          adminService.getAllAdminUsers(),
          adminService.getAllCities(),
        ]);
        setEntries(log);
        setAdmins(adminList);
        setCities(cityList);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const adminName = (uid: string) => admins.find((a) => a.uid === uid)?.name || uid;
  const cityName = (id?: string) => (id ? cities.find((c) => c.id === id)?.name || id : undefined);

  const impersonationCount = entries.filter((e) => e.isImpersonating).length;

  return (
    <Box>
      <PageHeader title="Audit Log" subtitle="Every admin action across every city — who did what, and when" />

      <Box sx={{ mb: 3, display: 'flex', gap: 2.5, flexWrap: 'wrap' }}>
        <Box sx={{ minWidth: 200 }}>
          <StatCard label="Total Entries" value={entries.length} icon={<History />} tone="primary" />
        </Box>
        <Box sx={{ minWidth: 200 }}>
          <StatCard label="While Impersonating" value={impersonationCount} icon={<History />} tone="warning" />
        </Box>
      </Box>

      <ResponsiveTable<AdminActivityLogEntry>
        rows={entries}
        rowKey={(e) => e.id}
        loading={loading}
        emptyTitle="No activity logged yet"
        aria-label="Audit log"
        columns={[
          {
            key: 'admin',
            header: 'Admin',
            mobile: 'title',
            render: (e) => adminName(e.adminId),
          },
          {
            key: 'action',
            header: 'Action',
            mobile: 'subtitle',
            render: (e) => e.action,
          },
          {
            key: 'context',
            header: 'Context',
            mobile: 'body',
            render: (e) =>
              e.isImpersonating ? (
                <Chip size="small" color="warning" label={`Viewing as ${cityName(e.actingAsCityId) || 'a city'}`} />
              ) : e.cityId ? (
                <Typography variant="body2">{cityName(e.cityId)}</Typography>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  —
                </Typography>
              ),
          },
          {
            key: 'timestamp',
            header: 'When',
            mobile: 'body',
            render: (e) => e.timestamp.toLocaleString(),
          },
        ]}
      />
    </Box>
  );
};

export default AuditLog;
